import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import tinycolor from "tinycolor2";
import { useColorPicker } from "../../../../hooks/useColorPicker.js";
import { clamp } from "../../utils.js";
const ratio = 150 / 256;
function useSaturation(args) {
  const defaultSaturationSize = {
    height: ratio * args.defaultWidth,
    width: args.defaultWidth
  };
  const { initialSize = defaultSaturationSize, selfColor, setSelfColor } = args;
  const { width, height } = initialSize;
  const [saturationSize, setSaturationSize] = useState(() => initialSize);
  const { onChange } = useColorPicker();
  const saturationRef = useRef(null);
  const saturationPosition = useMemo(() => {
    const hsv = selfColor.toHsv();
    return {
      x: hsv.s * saturationSize.width,
      y: (1 - hsv.v) * saturationSize.height
    };
  }, [selfColor, saturationSize]);
  const onMoveSaturation = useCallback(
    ({ x: rawX, y: rawY }) => {
      const x = clamp(rawX, saturationSize.width, 1);
      const y = clamp(rawY, saturationSize.height - 1, 0);
      const currentHexColor = selfColor.toHexString();
      const newHsv = {
        a: selfColor.toHsv().a,
        h: selfColor.toHsv().h,
        s: x / saturationSize.width,
        v: 1 - y / saturationSize.height
      };
      const newColor = tinycolor(newHsv);
      setSelfColor(newColor);
      if (newColor.toHexString() === currentHexColor || !newColor.isValid()) return;
      onChange(null, newColor.toHexString());
    },
    [selfColor, saturationSize, onChange, setSelfColor]
  );
  useLayoutEffect(() => {
    setSaturationSize(saturationRef.current?.getBoundingClientRect() ?? { height: 0, width: 0 });
  }, []);
  const saturationRect = saturationRef.current?.getBoundingClientRect() ?? {
    height: 0,
    width: 0
  };
  useLayoutEffect(() => {
    if (saturationSize.width === saturationRect.width && saturationSize.height === saturationRect.height) return;
    setSaturationSize({
      height: saturationRect.height,
      width: saturationRect.width
    });
  }, [saturationRect, saturationSize]);
  return useMemo(() => {
    const hueColor = `hsl(${selfColor.toHsl().h}, 100%, 50%)`;
    return {
      cursor: {
        backgroundColor: selfColor.toHexString(),
        left: saturationPosition.x,
        top: saturationPosition.y
      },
      onMoveSaturation,
      ref: saturationRef,
      styles: {
        backgroundColor: hueColor,
        height,
        width
      }
    };
  }, [selfColor, onMoveSaturation, saturationPosition, width, height]);
}
export {
  useSaturation
};
//# sourceMappingURL=useSaturation.js.map
